import { useState, useEffect, useRef } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { ChevronDown } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { BusinessTermsContent, BUSINESS_TERMS_VERSION } from "./BusinessTermsContent";
import { CustomerTermsContent, CUSTOMER_TERMS_VERSION } from "./CustomerTermsContent";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

interface TermsModalProps {
  open?: boolean;
  isOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
  onClose?: () => void;
  userType: "customer" | "practitioner" | "business" | "solo" | "center_admin" | "staff" | "manager" | string;
  email?: string;
  onAccept: () => void;
}

export function TermsModal({
  open,
  isOpen,
  onOpenChange,
  onClose,
  userType,
  email,
  onAccept,
}: TermsModalProps) {
  // Support both prop patterns
  const isDialogOpen = open ?? isOpen ?? false;
  const handleOpenChange = (newOpen: boolean) => {
    if (onOpenChange) onOpenChange(newOpen);
    if (!newOpen && onClose) onClose();
  };
  const [agreed, setAgreed] = useState(false);
  const [hasScrolledToBottom, setHasScrolledToBottom] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);

  // Reset state when modal opens
  useEffect(() => {
    if (isDialogOpen) {
      setAgreed(false);
      setHasScrolledToBottom(false);
    }
  }, [isDialogOpen]);

  const handleScroll = (event: React.UIEvent<HTMLDivElement>) => {
    const target = event.currentTarget;
    const isAtBottom = target.scrollHeight - target.scrollTop <= target.clientHeight + 50;
    if (isAtBottom) {
      setHasScrolledToBottom(true);
    }
  };

  const getDeviceInfo = () => {
    const ua = navigator.userAgent;
    const platform = navigator.platform;
    const language = navigator.language;
    
    return {
      userAgent: ua,
      platform,
      language,
      screenWidth: window.screen.width,
      screenHeight: window.screen.height,
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    };
  };

  const handleAccept = async () => {
    if (!agreed) return;
    
    setIsSubmitting(true);
    
    try {
      const deviceInfo = getDeviceInfo();
      const isBusinessType = userType === "practitioner" || userType === "business" || userType === "solo" || userType === "center_admin" || userType === "staff" || userType === "manager";
      const termsVersion = isBusinessType ? BUSINESS_TERMS_VERSION : CUSTOMER_TERMS_VERSION;

      // Get IP address (best effort - may be blocked by browser)
      let ipAddress = null;
      try {
        const ipResponse = await fetch("https://api.ipify.org?format=json");
        const ipData = await ipResponse.json();
        ipAddress = ipData.ip;
      } catch {
        // IP fetch failed, continue without it
      }

      if (email) {
        const { error } = await supabase.from("terms_acceptances").insert({
          email,
          user_type: userType,
          terms_version: termsVersion,
          privacy_version: "1.0",
          ip_address: ipAddress,
          user_agent: navigator.userAgent,
          device_info: deviceInfo,
        });

        if (error) {
          console.error("Error logging terms acceptance:", error);
          // Continue with registration even if logging fails
        }
      }

      onAccept();
      handleOpenChange(false);
    } catch (error) {
      console.error("Error during terms acceptance:", error);
      toast.error("An error occurred. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const isBusinessType = userType === "practitioner" || userType === "business" || userType === "solo" || userType === "center_admin" || userType === "staff" || userType === "manager";
  const title = isBusinessType
    ? "Business Terms and Conditions"
    : "Customer Terms and Conditions";

  return (
    <Dialog open={isDialogOpen} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-2xl max-h-[85dvh] sm:max-h-[85vh] overflow-hidden p-4 sm:p-6 flex flex-col">
        <DialogHeader className="flex-shrink-0">
          <DialogTitle className="font-display text-xl">{title}</DialogTitle>
        </DialogHeader>

        {/* iOS-friendly scrolling container with scroll indicator inside */}
        <div 
          ref={scrollRef}
          className="flex-1 overflow-y-auto pr-4 -mr-2 overscroll-contain min-h-0"
          style={{ 
            WebkitOverflowScrolling: "touch",
            touchAction: "pan-y",
          }}
          onScroll={handleScroll}
        >
          {/* Scroll indicator - sticky inside scroll area */}
          {!hasScrolledToBottom && (
            <div className="sticky top-0 z-10 flex items-center justify-center gap-2 py-2 text-xs sm:text-sm text-muted-foreground bg-muted/90 backdrop-blur-sm rounded-md mb-3">
              <ChevronDown className="h-3 w-3 sm:h-4 sm:w-4 animate-bounce" />
              <span>Scroll down to read all terms</span>
              <ChevronDown className="h-3 w-3 sm:h-4 sm:w-4 animate-bounce" />
            </div>
          )}
          <div className="prose prose-sm dark:prose-invert max-w-none pb-4">
            {isBusinessType ? <BusinessTermsContent /> : <CustomerTermsContent />}
          </div>
        </div>

        {/* Fixed consent section - compact on mobile */}
        <div className="flex-shrink-0 border-t border-border pt-2 sm:pt-3 space-y-2 sm:space-y-3">
          <div 
            className={`flex items-start space-x-2 sm:space-x-3 p-2 sm:p-3 rounded-lg border border-border bg-background touch-manipulation cursor-pointer ${!hasScrolledToBottom ? 'opacity-50' : ''}`}
            onClick={() => hasScrolledToBottom && setAgreed(!agreed)}
          >
            <Checkbox
              id="terms-agreement"
              checked={agreed}
              onCheckedChange={(checked) => hasScrolledToBottom && setAgreed(checked === true)}
              disabled={!hasScrolledToBottom}
              className="h-4 w-4 sm:h-5 sm:w-5 mt-0.5 flex-shrink-0"
            />
            <Label
              htmlFor="terms-agreement"
              className={`text-xs sm:text-sm leading-relaxed cursor-pointer flex-1 ${
                !hasScrolledToBottom ? "text-muted-foreground" : "text-foreground"
              }`}
            >
              I have read and agree to the{" "}
              <span className="font-medium text-primary">Terms and Conditions</span> and{" "}
              <span className="font-medium text-primary">Privacy Policy</span>. I consent to the 
              collection, use, storage, and disclosure of my personal information in accordance 
              with the Privacy Act 1988 (Cth) and the applicable privacy laws.
            </Label>
          </div>
        </div>

        <DialogFooter className="flex-shrink-0 pt-3 flex flex-col-reverse sm:flex-row sm:justify-end gap-2">
          <Button
            variant="outline"
            onClick={() => handleOpenChange(false)}
            disabled={isSubmitting}
            className="touch-manipulation w-full sm:w-auto min-h-[44px]"
          >
            Cancel
          </Button>
          <Button
            variant="hero"
            onClick={handleAccept}
            disabled={!agreed || isSubmitting}
            className="touch-manipulation w-full sm:w-auto min-h-[44px]"
          >
            {isSubmitting ? "Processing..." : "I Agree & Continue"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
